import { useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useSendTemplate } from '@/hooks/useMessaging';
import { whatsappTemplatesApi } from '@/lib/api/whatsapp-templates.api';
import { cn, extractErrorMessage } from '@/lib/utils';

import type { WhatsAppTemplate, TemplateComponent } from '@/lib/api/whatsapp-templates.api';

interface TemplateComposerProps {
  conversationId: string;
  configurationId: string;
  onSent: () => void;
}

const getComponent = (
  template: WhatsAppTemplate,
  type: 'HEADER' | 'BODY',
): TemplateComponent | undefined => {
  const components = template.components;
  if (!Array.isArray(components)) return undefined;
  return (components as TemplateComponent[]).find((c) => c?.type === type);
};

const extractPlaceholders = (text: string): string[] => {
  const keys: string[] = [];
  for (const match of text.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)) {
    if (!keys.includes(match[1])) keys.push(match[1]);
  }
  const allNumeric = keys.every((k) => /^\d+$/.test(k));
  return allNumeric ? keys.sort((a, b) => Number(a) - Number(b)) : keys;
};

const renderText = (text: string, values: Record<string, string>): string =>
  text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key: string) => values[key] ?? `{{${key}}}`);

const scoped = (section: 'header' | 'body', values: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(values)
      .filter(([k]) => k.startsWith(`${section}:`))
      .map(([k, v]) => [k.slice(section.length + 1), v]),
  );

const buildParameters = (keys: string[], values: Record<string, string>) => {
  const positional = keys.every((k) => /^\d+$/.test(k));
  return keys.map((key) =>
    positional
      ? { type: 'text', text: (values[key] ?? '').trim() }
      : { type: 'text', parameter_name: key, text: (values[key] ?? '').trim() },
  );
};

const formatTemplateName = (name: string): string =>
  name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const TemplateComposer = ({
  conversationId,
  configurationId: _configurationId,
  onSent,
}: TemplateComposerProps) => {
  const qc = useQueryClient();
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const sendTemplate = useSendTemplate();

  // Only templates on the WhatsApp account of this conversation's number.
  const { data: templates, isLoading } = useQuery({
    queryKey: ['whatsapp-templates', { conversationId }],
    queryFn: () => whatsappTemplatesApi.list({ conversationId }),
  });

  const approvedTemplates = templates?.filter((t) => t.status === 'APPROVED') ?? [];
  const selected = approvedTemplates.find((t) => t.name === selectedName);

  const headerComponent = selected ? getComponent(selected, 'HEADER') : undefined;
  const bodyComponent = selected ? getComponent(selected, 'BODY') : undefined;
  const hasMediaHeader = !!headerComponent?.format && headerComponent.format !== 'TEXT';
  const headerText = !hasMediaHeader ? (headerComponent?.text ?? '') : '';
  const bodyText = bodyComponent?.text ?? '';

  const sections = [
    {
      section: 'header' as const,
      label: 'Header',
      keys: extractPlaceholders(headerText),
      examples: headerComponent?.example?.header_text ?? [],
    },
    {
      section: 'body' as const,
      label: 'Body',
      keys: extractPlaceholders(bodyText),
      examples: bodyComponent?.example?.body_text?.[0] ?? [],
    },
  ].filter((s) => s.keys.length > 0);

  const allFilled = sections.every((s) =>
    s.keys.every((key) => variables[`${s.section}:${key}`]?.trim()),
  );
  const canSend = !!selected && !hasMediaHeader && (sections.length === 0 || allFilled);

  const handleSelect = (name: string) => {
    setSelectedName(name === selectedName ? null : name);
    setVariables({});
  };

  const handleSend = () => {
    if (!selected || !canSend) return;

    const headerValues = scoped('header', variables);
    const bodyValues = scoped('body', variables);
    const componentList: Array<Record<string, unknown>> = [];
    const headerKeys = extractPlaceholders(headerText);
    const bodyKeys = extractPlaceholders(bodyText);

    if (headerKeys.length > 0) {
      componentList.push({ type: 'header', parameters: buildParameters(headerKeys, headerValues) });
    }
    if (bodyKeys.length > 0) {
      componentList.push({ type: 'body', parameters: buildParameters(bodyKeys, bodyValues) });
    }

    const renderedParts = [
      headerText ? renderText(headerText, headerValues) : '',
      bodyText ? renderText(bodyText, bodyValues) : '',
    ].filter(Boolean);
    const renderedText = renderedParts.length > 0 ? renderedParts.join('\n') : undefined;

    sendTemplate.mutate(
      {
        conversationId,
        templateName: selected.name,
        language: selected.language ?? 'en_US',
        components: componentList.length > 0 ? componentList : undefined,
        renderedText,
      },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: ['messaging-messages', conversationId] });
          qc.invalidateQueries({ queryKey: ['messaging-conversations'] });
          qc.invalidateQueries({ queryKey: ['contacts'] });
          onSent();
        },
      },
    );
  };

  return (
    <div className="space-y-3 px-3 py-3">
      {/* Warning banner */}
      <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
        <span className="text-xs text-amber-200/90">
          24h window expired — send a template to continue
        </span>
      </div>

      {/* Template cards */}
      {isLoading ? (
        <div className="text-muted-foreground py-4 text-center text-xs">Loading templates...</div>
      ) : approvedTemplates.length === 0 ? (
        <div className="text-muted-foreground py-4 text-center text-xs">
          No approved templates on this number&apos;s WhatsApp account
        </div>
      ) : (
        <div className="max-h-48 space-y-1.5 overflow-y-auto">
          {approvedTemplates.map((t) => {
            const isSelected = selectedName === t.name;
            const body = getComponent(t, 'BODY');
            const preview = body?.text ?? '';
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleSelect(t.name)}
                className={cn(
                  'w-full rounded-lg border px-3 py-2 text-left transition-colors',
                  'bg-[#1a2730] hover:bg-[#1e2f3a]',
                  isSelected
                    ? 'border-blue-500 ring-1 ring-blue-500/40'
                    : 'border-[#2a3942] hover:border-[#3a4f5a]',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-[#e9edef]">
                    {formatTemplateName(t.name)}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className="rounded bg-[#2a3942] px-1.5 py-0.5 text-[10px] text-[#8696a0]">
                      {t.language}
                    </span>
                    {isSelected && <Check className="h-3.5 w-3.5 text-blue-400" />}
                  </div>
                </div>
                {preview && (
                  <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-[#8696a0]">
                    {preview}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Variable inputs for selected template */}
      {selected && hasMediaHeader && (
        <p className="text-xs text-red-400">
          This template has a {headerComponent?.format?.toLowerCase()} header which is not supported
          here — pick a text-only template.
        </p>
      )}

      {selected &&
        !hasMediaHeader &&
        sections.map((s) => (
          <div key={s.section} className="space-y-1.5">
            {sections.length > 1 && (
              <p className="text-[10px] font-semibold tracking-wide text-[#8696a0] uppercase">
                {s.label} variables
              </p>
            )}
            <div className="grid gap-1.5 sm:grid-cols-2">
              {s.keys.map((key, i) => {
                const stateKey = `${s.section}:${key}`;
                return (
                  <div key={stateKey} className="flex items-center gap-1.5">
                    <span className="shrink-0 rounded bg-[#2a3942] px-1.5 py-0.5 font-mono text-[10px] text-[#8696a0]">
                      {`{{${key}}}`}
                    </span>
                    <input
                      className="h-7 flex-1 rounded-md border border-[#2a3942] bg-[#111b21] px-2 text-xs text-[#e9edef] placeholder-[#8696a0] focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 focus:outline-none"
                      placeholder={s.examples[i] ?? `Value for {{${key}}}`}
                      value={variables[stateKey] ?? ''}
                      onChange={(e) => setVariables((v) => ({ ...v, [stateKey]: e.target.value }))}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}

      {/* Preview */}
      {selected && !hasMediaHeader && (headerText || bodyText) && (
        <div className="space-y-1 rounded-md border border-[#2a3942] bg-[#111b21]/60 px-2.5 py-1.5 text-xs leading-relaxed whitespace-pre-wrap">
          {headerText && (
            <p className="font-semibold text-[#e9edef]/80">
              {renderText(headerText, scoped('header', variables))}
            </p>
          )}
          {bodyText && (
            <p className="text-[#8696a0]">{renderText(bodyText, scoped('body', variables))}</p>
          )}
        </div>
      )}

      {/* Send button */}
      <Button
        onClick={handleSend}
        disabled={!canSend || sendTemplate.isPending}
        className="h-8 w-full gap-1.5 bg-[#00a884] text-sm font-medium text-white hover:bg-[#00a884]/90 disabled:opacity-40"
      >
        <Send className="h-3.5 w-3.5" />
        {sendTemplate.isPending ? 'Sending...' : 'Send Template'}
      </Button>
      {sendTemplate.isError && (
        <p className="text-xs text-red-400">
          {extractErrorMessage(sendTemplate.error, 'Failed to send template')}
        </p>
      )}
    </div>
  );
};
